/**
 * Creates a translation loader for a specific path
 * @param path The path to the translations directory
 * @returns A translation loader object
 */
export const createTranslationLoader = (path: string) => {
  return ['en_au', 'es', 'en_us'].reduce(
    (acc: { [key: string]: () => Promise<any> }, lang) => {
      acc[lang] = () =>
        import(/* webpackChunkName: "i18n-[request]" */ `${path}/${lang}.json`);
      return acc;
    },
    {}
  );
};

/**
 * Creates a translation loader that merges translations from multiple paths
 * @param paths Array of paths to translation directories
 * @returns A merged translation loader object
 */
export const createMultiPathTranslationLoader = (paths: string[]) => {
  const languages = ['en_au', 'es', 'en_us'];

  return languages.reduce(
    (acc: { [key: string]: () => Promise<any> }, lang) => {
      acc[lang] = async () => {
        try {
          const translations = await Promise.all(
            paths.map((path) =>
              import(
                /* webpackChunkName: "i18n-[request]" */ `${path}/${lang}.json`
              ).catch((error) => {
                console.warn(
                  `Failed to load translation: ${path}/${lang}.json`,
                  error
                );
                return { default: {} };
              })
            )
          );

          return translations.reduce((merged, translation) => {
            return { ...merged, ...(translation.default || translation) };
          }, {});
        } catch (error) {
          console.error(`Error loading translations for ${lang}:`, error);
          return {};
        }
      };
      return acc;
    },
    {}
  );
};

// Default driver translations loader
export const driverTranslations = createMultiPathTranslationLoader([
  './settings/i18n',
  './detail/i18n',
]);

// Returns only the driver settings translations loader
export const getDriverSettingsTranslations = () => {
  return createMultiPathTranslationLoader(['./settings/i18n']);
};

// Helper to merge multiple translation loaders
export const mergeTranslationLoaders = (
  ...loaders: { [key: string]: () => Promise<any> }[]
) => {
  const result: { [key: string]: () => Promise<any> } = {};

  for (const loader of loaders) {
    for (const [lang, loadFn] of Object.entries(loader)) {
      if (!result[lang]) {
        result[lang] = loadFn;
      } else {
        const existingLoadFn = result[lang];
        result[lang] = async () => {
          const [existing, current] = await Promise.all([
            existingLoadFn(),
            loadFn(),
          ]);
          return { ...existing, ...current };
        };
      }
    }
  }

  return result;
};
